/**
 * 同步 SHA-256(hex) —— 同构(node/浏览器都可跑), 零依赖。
 *
 * 为什么不用 Web Crypto `crypto.subtle.digest`:
 *  PoW 客户端需要在一个 10^5 级的紧循环里反复算 sha256 找解, subtle.digest 只有异步 API,
 *  每次一个 await, 10 万次 await 会又慢又卡 UI(还得处理国产 WebView 的 secure-context 差异)。
 *  这里用一份经典的同步实现, 在 Web Worker 里跑(不可用时退回主线程分片)。
 *
 * 服务端(pow.ts)校验用 node:crypto(更快), 二者对同一输入产出一致的 sha256hex — 已用测试校验。
 *
 * ⚠️ **整份实现刻意包在 `createSha256()` 这一个自包含闭包里**, 不要把 K/HEX/utf8Bytes/rotr
 * 提回模块作用域。PoW 的 Web Worker 源码是靠 `createSha256.toString()` 派生出来的
 * (见 pow/solver.ts): 只有当函数体不引用任何外部标识符时, 序列化进 Worker 才成立, 且
 * 消费方打包器 minify 重命名内部变量也不影响 —— 闭包内是一致重命名。一旦把常量提到模块级,
 * Worker 里就会 ReferenceError, 而主线程 fallback 仍然正常, 于是**只在手机上偶发失败**,
 * 类型检查和测试全绿。这正是本模块最难查的一类故障, 别动。
 *
 * 这段最初来自 yanzhenma 验证码项目(该仓已于 2026-08-23 删除, 能力并入本仓)。
 */
/**
 * 造一个 sha256hex 实现。自包含: 函数体内不引用任何外部标识符, 可安全 `toString()` 进 Worker。
 * 常量在工厂调用时建一次, 返回的闭包反复用 —— 紧循环里不会重复分配 K。
 */
export function createSha256() {
    const K = new Uint32Array([
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ]);
    const HEX = "0123456789abcdef";
    function utf8Bytes(str) {
        // 输入通常是 hex salt + 十进制 number(纯 ASCII), 但用标准 UTF-8 编码保证任意输入正确
        if (typeof TextEncoder !== "undefined")
            return new TextEncoder().encode(str);
        // 极老环境兜底
        const out = [];
        for (let i = 0; i < str.length; i++) {
            const c = str.charCodeAt(i);
            if (c < 0x80)
                out.push(c);
            else if (c < 0x800)
                out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
            else
                out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
        }
        return new Uint8Array(out);
    }
    function rotr(x, n) {
        return (x >>> n) | (x << (32 - n));
    }
    return function sha256hex(message) {
        const msg = utf8Bytes(message);
        const bitLen = msg.length * 8;
        // padding: 0x80, 补 0 到 56 (mod 64), 末尾 8 字节大端长度
        const withOne = msg.length + 1;
        const padded = new Uint8Array(Math.ceil((withOne + 8) / 64) * 64);
        padded.set(msg);
        padded[msg.length] = 0x80;
        // 64-bit big-endian length(仅写低 32 位, 消息长度不会超)
        const dv = new DataView(padded.buffer);
        dv.setUint32(padded.length - 4, bitLen >>> 0, false);
        dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000), false);
        const H = new Uint32Array([
            0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
        ]);
        const w = new Uint32Array(64);
        // ⚠️ 下面所有下标读取都写了 `!`：Uint32Array/常量表的长度在本函数里是定死的，
        // 越界不可能发生。但消费方（V7/5FD 等）开了 `noUncheckedIndexedAccess`，
        // 不写断言的话它们 typecheck 会在这个文件里炸出几十条 "possibly undefined" ——
        // 而本包是 TS 源码分发的，消费方的 tsc 会真的编译这里。`!` 是零运行时开销的写法，
        // 换成 `?? 0` 之类反而会在 PoW 求解的热循环里加分支。
        for (let off = 0; off < padded.length; off += 64) {
            for (let i = 0; i < 16; i++)
                w[i] = dv.getUint32(off + i * 4, false);
            for (let i = 16; i < 64; i++) {
                const w15 = w[i - 15], w2 = w[i - 2];
                const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
                const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
                w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
            }
            let a = H[0], b = H[1], c = H[2], d = H[3], e = H[4], f = H[5], g = H[6], h = H[7];
            for (let i = 0; i < 64; i++) {
                const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
                const ch = (e & f) ^ (~e & g);
                const t1 = (h + S1 + ch + K[i] + w[i]) | 0;
                const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
                const maj = (a & b) ^ (a & c) ^ (b & c);
                const t2 = (S0 + maj) | 0;
                h = g;
                g = f;
                f = e;
                e = (d + t1) | 0;
                d = c;
                c = b;
                b = a;
                a = (t1 + t2) | 0;
            }
            H[0] = (H[0] + a) | 0;
            H[1] = (H[1] + b) | 0;
            H[2] = (H[2] + c) | 0;
            H[3] = (H[3] + d) | 0;
            H[4] = (H[4] + e) | 0;
            H[5] = (H[5] + f) | 0;
            H[6] = (H[6] + g) | 0;
            H[7] = (H[7] + h) | 0;
        }
        let hex = "";
        for (let i = 0; i < 8; i++) {
            const v = H[i];
            hex +=
                HEX[(v >>> 28) & 0xf] + HEX[(v >>> 24) & 0xf] + HEX[(v >>> 20) & 0xf] + HEX[(v >>> 16) & 0xf] +
                    HEX[(v >>> 12) & 0xf] + HEX[(v >>> 8) & 0xf] + HEX[(v >>> 4) & 0xf] + HEX[v & 0xf];
        }
        return hex;
    };
}
export const sha256hex = createSha256();
